import { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { GetStaticProps } from "next";
import Layout from "../../components/layout";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../next-i18next.config";
import "../../styles/scss/pricing.module.scss";
import "../../styles/scss/brand-assets.module.scss";

const NAV_ITEMS = [
  { id: "naming", label: "Naming" },
  { id: "spacing", label: "Spacing" },
  { id: "wordmark", label: "Wordmark" },
  { id: "logomark", label: "Logomark" },
  { id: "colors", label: "Colors" },
  { id: "collaboration", label: "Collaboration" },
  { id: "things-to-avoid", label: "Things to Avoid" },
  { id: "backgrounds", label: "Backgrounds" },
] as const;

const BRAND_COLORS = [
  { hex: "#5B5FC7" },
  { hex: "#EFEFF9" },
  { hex: "#191A23" },
  { hex: "#343F4F" },
  { hex: "#F2F3F4" },
] as const;

const DownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 2.5V12.5M10 12.5L6.25 8.75M10 12.5L13.75 8.75M3.75 14.375V15.625C3.75 16.6605 4.58947 17.5 5.625 17.5H14.375C15.4105 17.5 16.25 16.6605 16.25 15.625V14.375"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type DownloadButtonProps = {
  href: string;
  label: string;
  filename: string;
};

const DownloadButton = ({ href, label, filename }: DownloadButtonProps) => (
  <a href={href} download={filename} className="brand-download-btn">
    <DownloadIcon />
    {label}
  </a>
);

type ColorCardProps = {
  hex: string;
};

const ColorCard = ({ hex }: ColorCardProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable
    }
  }, [hex]);

  return (
    <button
      type="button"
      className={`color-card${copied ? " copied" : ""}`}
      onClick={handleCopy}
      aria-label={`Copy color ${hex}`}
    >
      <span className="color-swatch" style={{ backgroundColor: hex }} />
      <span className="color-code">{copied ? "Copied!" : hex}</span>
    </button>
  );
};

type BrandAssetImageProps = {
  src: string;
  alt: string;
  unoptimized?: boolean;
};

const BrandAssetImage = ({ src, alt }: BrandAssetImageProps) => (
  <div className="brand-asset-image">
    <Image src={src} alt={alt} width={880} height={400} />
  </div>
);

export default function BrandAssetsPage() {
  const [activeSection, setActiveSection] = useState<string>(NAV_ITEMS[0].id);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 120;
      let current: string = NAV_ITEMS[0].id;

      NAV_ITEMS.forEach(({ id }) => {
        const element = document.getElementById(id);
        if (element && element.offsetTop <= scrollPosition) {
          current = id;
        }
      });

      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 90,
        behavior: "smooth",
      });
      setActiveSection(id);
    }
  };

  return (
    <Layout
      isHomePage={false}
      pageTitle="Middleware Brand Assets — Logos, Colors & Downloads"
      ogImage="/images/thing-to-avoid.svg"
      uri="/brand-assets/"
      metaTitle="Middleware Brand Assets — Logos, Colors & Downloads"
      metaDesc="Download official Middleware logos, brand colors and background graphics. Everything partners, press, and contributors need to use Middleware assets correctly."
      metaRobotsNoindex="index"
      metaRobotsNofollow="follow"
    >
      <div className="pricing-page-v2 brand-assets-page">
        <div className="brand-assets-hero pricing-hero">
          <div className="container">
            <h1>
              <span className="text-gradient">Middleware </span> Brand Assets
            </h1>
            <p>Download official logos, colors, and brand assets for consistent use. </p>
          </div>
        </div>

        <div className="brand-guidelines-banner">
          <div className="container">
            <h2>About these assets</h2>
            <p>The Middleware brand appears across product experiences, partnerships, media coverage, community content, and marketing materials. These assets help ensure that Middleware is represented accurately and consistently across every channel.</p><br/>
            <p>Middleware trademarks include the company name, logos, product names, and other brand identifiers. Do not modify or use these assets in a way that implies sponsorship, endorsement, or affiliation without prior written permission. Proper trademark usage helps maintain brand consistency, clarity, and trust.</p>
          </div>
        </div>

        <div className="brand-assets-content">
          <div className="container">
            <div className="d-flex">
              <aside className="left-block">
                <div className="brand-nav-sidebar">
                  <p className="resources-label">Resources</p>
                  <ul className="brand-nav-list">
                    {NAV_ITEMS.map(({ id, label }) => (
                      <li key={id} className={activeSection === id ? "active" : ""}>
                        <button type="button" onClick={() => scrollToSection(id)}>
                          {label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>

              <main className="right-block">
                <section id="naming" className="brand-section">
                  <h2>Naming</h2>
                  <div className="section-text">
                    <p>"Middleware" is a single word and should always be written with a capital <b>M</b> followed by lowercase letters. It refers to both the company and the observability platform. The legal company name is <b>Middleware Inc.</b> Always use the correct spelling <b>Middleware</b> and avoid variations such as MiddleWare, middleware, MIDDLEWARE, or Middle Ware.</p>
                    <p>When referencing Middleware products, use their official names exactly as written. Use <b>Middleware</b> when referring to the platform or company and <b>Middleware OpsAI</b> when referring to the AI SRE agent. Avoid incorrect variations such as MW, ops ai, OpsAi, Ops A.I., or opsai.</p>
                    <p>In public-facing content, use the full product name on first mention. Subsequent references may use the shortened form <b>OpsAI</b>. Product names should never be abbreviated, modified, or altered in a way that could create confusion or misrepresent the Middleware brand.</p>
                  </div>
                </section>

                <section id="spacing" className="brand-section">
                  <h2>Spacing</h2>
                  <div className="section-text">
                    <p>Maintain a minimum clear space of 40 units (points, pixels, or the relevant measurement for the medium) around the Middleware logo at all times. This applies to both the wordmark and the logomark in all contexts digital, print, co-branded materials, social assets, and event collateral.</p>
                  </div>
                  <BrandAssetImage src="/images/spacing.svg" alt="Middleware logo spacing guidelines" />
                </section>

                <section id="wordmark" className="brand-section">
                  <h2>Wordmark</h2>
                  <div className="section-text">
                    <p>The Middleware primary wordmark is the most recognizable representation of our brand. It should be used in all official communications, marketing materials, product interfaces, and partner references wherever possible.</p>
                    <p>Use the appropriate version for your background:</p>

                    <table>
                      <thead>
                        <tr>
                          <th>Version</th>
                          <th>Background</th>
                          <th>Use case</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Full color</td>
                          <td>White or light</td>
                          <td>Default for all digital and print</td>
                        </tr>
                        <tr>
                          <td>Monochrome black</td>
                          <td>White or light</td>
                          <td>When color is not feasible</td>
                        </tr>
                        <tr>
                          <td>Monochrome white</td>
                          <td>Dark or colored</td>
                          <td>Reversed layouts, dark backgrounds, product UI</td>
                        </tr>
                      </tbody>
                    </table>

                    <p>Available file formats: PNG (transparent background), SVG (vector, scalable to any size). Use SVG for web and screen; use PNG when SVG is not supported.</p>
                  </div>
                  <div className="download-buttons">
                    <DownloadButton
                      href="/assets/Wordmark%20Logos.zip"
                      label="Download Logos (PNG)"
                      filename="Wordmark Logos.zip"
                    />
                    <DownloadButton
                      href="/assets/Wordmark%20SVG.zip"
                      label="Download Logos (SVG)"
                      filename="Wordmark SVG.zip"
                    />
                  </div>
                  <BrandAssetImage src="/images/wordmark.svg" alt="Middleware wordmark logo variations" />
                </section>

                <section id="logomark" className="brand-section">
                  <h2>Logomark</h2>
                  <div className="section-text">
                    <p>The Middleware logomark is a simplified symbol for compact applications: social media avatars, favicons, app icons, and product UI. Use the full-color version by default. Monochrome versions may be used when contrast or simplified styling requires it.</p>
                    <table>
                      <thead>
                        <tr>
                          <th>Context</th>
                          <th>Minimum size</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Favicon</td>
                          <td>16×16px</td>
                        </tr>
                        <tr>
                          <td>App icon</td>
                          <td>48×48px</td>
                        </tr>
                        <tr>
                          <td>Social media avatar</td>
                          <td>96×96px</td>
                        </tr>
                        <tr>
                          <td>Email signature</td>
                          <td>80px wide</td>
                        </tr>
                        <tr>
                          <td>Print (small format)</td>
                          <td>0.25 inches</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="download-buttons">
                    <DownloadButton
                      href="/assets/Logomark%20PNG.zip"
                      label="Download Logos (PNG)"
                      filename="Logomark PNG.zip"
                    />
                    <DownloadButton
                      href="/assets/Logomark%20SVG.zip"
                      label="Download Logos (SVG)"
                      filename="Logomark SVG.zip"
                    />
                  </div>
                  <BrandAssetImage src="/images/logomark.svg" alt="Middleware logomark logo variations" />
                </section>

                <section id="colors" className="brand-section">
                  <h2>Colors</h2>
                  <div className="section-text">
                    <p>Our brand colors reflect Middleware's clarity, precision, and modern engineering foundation. Use the approved palette consistently across all digital and print touchpoints.</p>
                    <p>Click a color to copy its hex code.</p>
                  </div>
                  <div className="color-cards">
                    {BRAND_COLORS.map(({ hex }) => (
                      <ColorCard key={hex} hex={hex} />
                    ))}
                  </div>
                </section>

                <section id="collaboration" className="brand-section">
                  <h2>Collaboration</h2>
                  <div className="section-text">
                    <p>When partnering with Middleware, both brands should be presented with equal visual weight and clear separation.</p>
                    <p>Guidelines for all co-branded materials:</p>
                    <ul>
                      <li>Maintain a minimum 40pt clear space on all sides of each logo.</li>
                      <li>Use a neutral divider (vertical rule or "&") between logos to ensure clarity.</li>
                      <li>Keep proportions balanced — neither logo should overpower the other.</li>
                      <li>Do not modify, stretch, recolor, or distort either logo.</li>
                      <li>Use monochrome versions when background contrast requires it.</li>
                    </ul>
                    <p>Approved lockups apply to: joint announcements, partnership pages, landing pages, press releases, integration pages, and event materials.</p>

                    <p>For co-branding, custom campaign usage, or any lockup not covered here, <Link href="/contact-us/">contact our brand team</Link> before going into production.</p>
                  </div>
                  <BrandAssetImage src="/images/collaboration.svg" alt="Middleware collaboration logo lockups" />
                </section>

                <section id="things-to-avoid" className="brand-section">
                  <h2>Things to Avoid</h2>
                  <div className="section-text">
                    <p>Do not alter, distort, rotate, flip, recolor, outline, or stretch the Middleware logo. Do not apply effects including shadows, gradients, or transparency. Do not place the logo inside shapes or containers, or over busy or noisy backgrounds that reduce clarity and readability.</p>
                    <p>Do not recreate the logo from memory or from a screenshot. Always download approved files from this page.</p>

                    <p>Always use approved logo files. Always maintain correct spacing and proportions.</p>
                  </div>
                  <BrandAssetImage
                    src="/images/thing-to-avoid.svg"
                    alt="Middleware logo usage examples to avoid"
                    unoptimized
                  />
                </section>

                <section id="backgrounds" className="brand-section">
                  <h2>Backgrounds</h2>
                  <div className="section-text">
                    <p>
                      Middleware background graphics are available for blog posts, marketing materials, and social media, designed to maintain visual consistency and strengthen brand recognition across all digital touchpoints.
                    </p>
                  </div>
                  <div className="download-buttons">
                    <DownloadButton
                      href="/assets/MW%20Backgrounds.zip"
                      label="Download All (PNG)"
                      filename="MW Backgrounds.zip"
                    />
                  </div>
                  <BrandAssetImage src="/images/backgrounds-mw.webp" alt="Middleware branded background graphics" />
                  <br/>

                  <p>For usage outside these guidelines, including co-branding, custom lockups, press inquiries, leadership headshot requests, or partner campaign materials, contact the Middleware brand team at hello@middleware.io before publishing or going into production.</p>
                </section>
              </main>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    pageData: "",
    pageTitle: "Middleware Brand Assets — Logos, Colors & Downloads",
    description:
      "Download official Middleware logos, brand colors and background graphics. Everything partners, press, and contributors need to use Middleware assets correctly.",
    uri: "/brand-assets/",
    ogImage: "/images/thing-to-avoid.svg",
    metaTitle: "Middleware Brand Assets — Logos, Colors & Downloads",
    metaDesc:
      "Download official Middleware logos, brand colors and background graphics. Everything partners, press, and contributors need to use Middleware assets correctly.",
    metaRobotsNoindex: "index",
    metaRobotsNofollow: "follow",
    ...(await serverSideTranslations(locale ?? "en", ["common", "header", "footer"], nextI18NextConfig)),
  },
});
